import React from 'react';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import { isEmpty } from 'lodash-es';
import { useSelector, shallowEqual } from 'react-redux';

import {
  YEAR,
  MILEAGE,
  ODOMETER,
  FilterPillsList,
  Mileage
} from '../../_shared/constants/filters';
import { TradeState, FilterNames } from '../../_shared/types/trade';
import Pill from '../../_shared/components/Pill';
import FilterService from '../../services/FilterService';

import { StyledFilterPillsWrapper } from './Styles';

const propTypes = {
  setIsFilterReset: PropTypes.func.isRequired,
  tradeState: PropTypes.number.isRequired,
  setFilterPillName: PropTypes.func.isRequired
};

const FilterPills = ({ setIsFilterReset, tradeState, setFilterPillName }) => {
  const intl = useIntl();

  const [filterMetaData, activeFilter] = useSelector(
    state => [state?.filter?.filterMetaData, state?.filter?.activeFilter],
    shallowEqual
  );

  const formatFilterPill = filterName => {
    const {
      locations,
      manufacturers,
      models,
      distances,
      fuelTypes,
      transmissionTypes,
      yearRange,
      mileageRange
    } = filterMetaData;

    let filterType;
    let formattedMessage;

    switch (filterName) {
      case FilterPillsList.Province: {
        formattedMessage = FilterService.formatFilterPillName(locations, {
          selected: 'list.filters.province'
        });
        break;
      }
      case FilterPillsList.Distance: {
        const distance = distances?.find(
          item => item.value !== -1 && item.isSelected
        ).value;

        formattedMessage = intl.formatMessage(
          {
            id: 'list.filters.pills.distance'
          },
          {
            number: distance
          }
        );
        break;
      }
      case FilterPillsList.ModelYear: {
        const yearStart = yearRange?.start;
        const yearEnd =
          yearRange?.end > YEAR.max ? `${YEAR.max}+` : yearRange?.end;

        filterType = intl.formatMessage({
          id: 'tr5.year'
        });

        const formattedBefore = intl.formatMessage(
          {
            id: 'list.filters.year.before'
          },
          {
            year: YEAR.min
          }
        );

        formattedMessage =
          yearStart > 0
            ? `${filterType}: ${yearStart}-${yearEnd}`
            : `${filterType}: ${formattedBefore}-${yearEnd}`;

        break;
      }
      case FilterPillsList.Make: {
        formattedMessage = FilterService.formatFilterPillName(manufacturers, {
          selected: 'list.filters.makes'
        });
        break;
      }
      case FilterPillsList.Model: {
        formattedMessage = FilterService.formatFilterPillName(
          models,
          {
            selected: 'list.filters.models'
          },
          true
        );
        break;
      }
      case FilterPillsList.MileageRange: {
        const mileageStart = mileageRange?.start?.toLocaleString();
        const mileageEnd =
          mileageRange?.end <= Mileage.ceil
            ? mileageRange?.end?.toLocaleString()
            : `${Mileage.ceil.toLocaleString()}+`;
        const formattedMileageRange = `${mileageStart}-${mileageEnd}`;

        formattedMessage = intl.formatMessage(
          {
            id: 'list.filters.pills.mileageRange'
          },
          {
            range: formattedMileageRange
          }
        );
        break;
      }
      case FilterPillsList.TransmissionType: {
        formattedMessage = FilterService.formatFilterPillName(
          transmissionTypes,
          {
            selected: 'list.filters.transmission'
          }
        );
        break;
      }
      case FilterPillsList.FuelType: {
        formattedMessage = FilterService.formatFilterPillName(fuelTypes, {
          selected: 'list.filters.fuelType'
        });
        break;
      }
      case FilterPillsList.OmitAsIs: {
        formattedMessage = intl.formatMessage({
          id: 'list.filters.conditions.asIs'
        });

        break;
      }
      case FilterPillsList.OmitHistory: {
        formattedMessage = intl.formatMessage({
          id: 'list.filters.conditions.any.history'
        });

        break;
      }
      case FilterPillsList.OmitAmount: {
        formattedMessage = intl.formatMessage({
          id: 'list.filters.conditions.accident.amount'
        });

        break;
      }
      case FilterPillsList.IsInStock: {
        formattedMessage = intl.formatMessage({
          id: 'list.filters.inStock'
        });
        break;
      }
      case FilterPillsList.IsFranchiseDealer: {
        formattedMessage = intl.formatMessage({
          id: 'list.filters.franchiseDealers'
        });
        break;
      }
      case FilterPillsList.IsFloorPriceMet: {
        formattedMessage = intl.formatMessage({
          id: 'list.filters.floorMet'
        });
        break;
      }
      default: {
        formattedMessage = '';
      }
    }
    return [formattedMessage];
  };

  const shouldHideFilterPills = () => {
    return (
      activeFilter?.id === FilterNames.watchlist.id ||
      activeFilter?.id === FilterNames.independent.id ||
      activeFilter?.id === FilterNames.franchise.id ||
      activeFilter?.id === FilterNames.impound.id
    );
  };

  const shouldDisplayFilterState = filterName => {
    let isDisplayed;

    if (!isEmpty(filterMetaData)) {
      const {
        locations,
        conditions,
        manufacturers,
        models,
        distances,
        fuelTypes,
        transmissionTypes,
        yearRange,
        mileageRange,
        tradeConditions,
        showDistance
      } = filterMetaData;

      switch (filterName) {
        case FilterPillsList.Province: {
          isDisplayed =
            !showDistance && FilterService.handleFilterPillDisplay(locations);
          break;
        }
        case FilterPillsList.Distance: {
          isDisplayed =
            showDistance &&
            !isEmpty(
              distances?.find(item => item.value !== -1 && item.isSelected)
            );
          break;
        }
        case FilterPillsList.ModelYear: {
          isDisplayed =
            yearRange?.start > YEAR.min || yearRange?.end < YEAR.max;
          break;
        }
        case FilterPillsList.Make: {
          isDisplayed = FilterService.handleFilterPillDisplay(manufacturers);
          break;
        }
        case FilterPillsList.Model: {
          isDisplayed = FilterService.handleFilterPillDisplay(models, true);

          break;
        }
        case FilterPillsList.MileageRange: {
          isDisplayed =
            mileageRange?.start > Math.min(MILEAGE.min, ODOMETER.min) ||
            mileageRange?.end < Math.min(MILEAGE.max, ODOMETER.max);

          break;
        }
        case FilterPillsList.TransmissionType: {
          isDisplayed = FilterService.handleFilterPillDisplay(
            transmissionTypes
          );
          break;
        }
        case FilterPillsList.FuelType: {
          isDisplayed = FilterService.handleFilterPillDisplay(fuelTypes);
          break;
        }
        case FilterPillsList.OmitAsIs: {
          isDisplayed = !isEmpty(
            conditions?.find(
              condition => condition.key === '0' && condition.isSelected
            )
          );
          break;
        }
        case FilterPillsList.OmitHistory: {
          isDisplayed = !isEmpty(
            conditions?.find(
              condition => condition.key === '1' && condition.isSelected
            )
          );
          break;
        }
        case FilterPillsList.OmitAmount: {
          isDisplayed = !isEmpty(
            conditions?.find(
              condition => condition.key === '2' && condition.isSelected
            )
          );
          break;
        }
        case FilterPillsList.IsInStock: {
          isDisplayed = tradeConditions?.isInStock;
          break;
        }
        case FilterPillsList.IsFloorPriceMet: {
          isDisplayed =
            tradeState === TradeState.active &&
            tradeConditions?.isFloorPriceMet;
          break;
        }
        case FilterPillsList.IsFranchiseDealer: {
          isDisplayed = tradeConditions?.isFranchiseDealer;
          break;
        }
        default: {
          isDisplayed = false;
        }
      }
    }

    return isDisplayed;
  };

  const removeCriteria = filterName => {
    setIsFilterReset(true);
    if (
      filterName !== FilterPillsList.Make ||
      filterName !== FilterPillsList.Model
    ) {
      setFilterPillName(filterName);
    } else if (filterName === FilterPillsList.Make) {
      setFilterPillName('make');
    } else if (filterName === FilterPillsList.Model) {
      setFilterPillName('model');
    }
  };

  const onEnterKey = (e, pill) => {
    if (e.key === 'Enter') {
      removeCriteria(pill);
    }
  };

  return (
    <StyledFilterPillsWrapper>
      {Object.values(FilterPillsList).map(pill => {
        return (
          !shouldHideFilterPills() &&
          shouldDisplayFilterState(pill) && (
            <React.Fragment key={pill}>
              <Pill
                testId={pill}
                variant="filter"
                message={formatFilterPill(pill)}
                onClick={e => {
                  e.preventDefault();
                  removeCriteria(pill);
                }}
                onKeyPress={e => {
                  onEnterKey(e, pill);
                }}
              />
            </React.Fragment>
          )
        );
      })}
    </StyledFilterPillsWrapper>
  );
};

FilterPills.propTypes = propTypes;

export default FilterPills;
